import { useRefract } from "refract-rxjs";

import { type ApolloResult } from "../../shared/models";
import { makeUTCStartEndTimestamp } from "../converters";
import {
    type LensOrganicStatsInput,
    type LensOrganicStatsOutput,
    combineIncompleteDateStats,
    lensOrganicStatsEffect,
} from "../effects";
import { mapInsightsComputedMetrics, mapInsightsMetrics } from "../mapping";
import { GranularityInDaysDifference, LensGranularity, metricsFields } from "../models";

import { useLensIdSearchParameter } from "./useLensIdSearchParameter";
import { useOrganicStatsQuery } from "./useOrganicStatsQuery";
import { useSubscribersTotal } from "./useSubscribersTotal";
import { useTimeRange } from "./useTimeRange";

export const useOrganicStats = (): ApolloResult<LensOrganicStatsOutput> => {
    const { selectedTimeRange: rawTimeRange, isAllTimeSelected } = useTimeRange();
    // Organic-stats upstream rejects requests > 90 days (SRS_VALIDATION-35).
    // The chart query asks for 2× the granularity (current + previous), so any
    // selected range over 45 days would blow the cap. Downgrade to Month-double
    // (56 days) for the chart series — the displayed total/diff still uses the
    // user's actual selection via useOrganicStatsTotal.
    const selectedTimeRange =
        rawTimeRange === LensGranularity.ThreeMonths || rawTimeRange === LensGranularity.TwoMonths
            ? LensGranularity.Month
            : rawTimeRange;

    const { lensId } = useLensIdSearchParameter();
    const { setSubscribersTotalValues } = useSubscribersTotal();

    const {
        data: chartsData,
        error: chartsError,
        loading: chartsLoading,
    } = useOrganicStatsQuery({
        metricFields: metricsFields,
        isTotal: false,
        isAllTimeSelected,
        startEndTimestamp: makeUTCStartEndTimestamp(GranularityInDaysDifference[selectedTimeRange]),
    });

    const {
        data: incompleteDateData,
        error: incompleteDateError,
        loading: incompleteDateLoading,
    } = useOrganicStatsQuery({
        metricFields: metricsFields,
        isTotal: false,
        isAllTimeSelected,
        startEndTimestamp: makeUTCStartEndTimestamp(2, 1),
    });

    const {
        data: lifetimeData,
        error: lifetimeError,
        loading: lifetimeLoading,
    } = useOrganicStatsQuery({
        metricFields: metricsFields,
        isTotal: true,
        isAllTimeSelected: true,
        startEndTimestamp: {
            startTime: null,
            endTime: null,
        },
        skip: selectedTimeRange !== LensGranularity.Lifetime,
    });

    const { chartsMetrics, chartsMetricsComputed, additionalInsights } = useRefract<
        LensOrganicStatsInput,
        LensOrganicStatsOutput
    >(lensOrganicStatsEffect, {
        chartsData,
        chartsLoading,
        lifetimeData,
        lifetimeLoading,
        selectedTimeRange,
        lensId,
        setSubscribersTotalValues,
    });

    const incompleteDateDataStats = mapInsightsMetrics(
        incompleteDateData?.getMyLensesOrganicStats.responseEntryList ?? [],
        metricsFields,
        true,
    );

    const incompleteDateDataComputedStats = mapInsightsComputedMetrics(
        incompleteDateData?.getMyLensesOrganicStats.responseEntryList ?? [],
        true,
    );

    return {
        chartsMetrics: combineIncompleteDateStats(chartsMetrics, incompleteDateDataStats.metrics),
        chartsMetricsComputed: combineIncompleteDateStats(chartsMetricsComputed, incompleteDateDataComputedStats),
        additionalInsights,
        loading: chartsLoading || lifetimeLoading || incompleteDateLoading,
        error: chartsError ?? lifetimeError ?? incompleteDateError,
    };
};
